import { useState } from "react";
import { Plus, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BookingStatusesList } from "./BookingStatusesList";
import { BookingStatusModal } from "./BookingStatusModal";

export function StatusManager() {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);

  return (
    <div className="space-y-4 sm:space-y-6">
      <TooltipProvider>
        <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg sm:text-xl">Booking Statuses</CardTitle>
              <CardDescription className="text-sm">
                Configure the available booking statuses in the system
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-md p-4" side="left">
                  <div className="space-y-2">
                    <h4 className="font-semibold">Booking Status Management</h4>
                    <p className="text-sm">Booking statuses are the <strong>foundation</strong> of the event orchestration system:</p>
                    <ul className="text-sm space-y-1 list-disc pl-4">
                      <li><strong>Status Categories:</strong> Group related statuses (initial, processing, completion, cancellation)</li>
                      <li><strong>Display Properties:</strong> Configure colors, names, and descriptions for user interfaces</li>
                      <li><strong>Sort Order:</strong> Control the sequence and priority of statuses in workflows</li>
                      <li><strong>Workflow Foundation:</strong> Each status serves as a potential trigger point for automated events</li>
                    </ul>
                    <p className="text-sm pt-2">These statuses define the possible states in your booking lifecycle and determine which transitions and events are available.</p>
                  </div>
                </TooltipContent>
              </Tooltip>
              <Button 
                onClick={() => setShowStatusModal(true)}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Status
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <BookingStatusesList
            onEdit={(statusId) => {
              setEditingStatus(statusId);
              setShowStatusModal(true);
            }}
          />
        </CardContent>
        </Card>

        <BookingStatusModal
          open={showStatusModal || !!editingStatus}
          onOpenChange={(open) => {
            if (!open) {
              setShowStatusModal(false);
              setEditingStatus(null);
            }
          }}
          statusId={editingStatus}
        />
      </TooltipProvider>
    </div>
  );
}